import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SearchResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]); 
  const [selectedBreeds, setSelectedBreeds] = useState([]);
  const [breeds, setBreeds] = useState([]);    
  const [priceRange, setPriceRange] = useState(10000000);
  const [minPrice, setMinPrice] = useState(0);
  const [sortBy, setSortBy] = useState('Price: High to Low');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeight, setSelectedWeight] = useState(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sellers, setSellers] = useState([]);
  const [selectedSellerId, setSelectedSellerId] = useState('');
  const [browseMode, setBrowseMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') || 'animals';
  });
  const [meatProducts, setMeatProducts] = useState([]);
  const [meatLoading, setMeatLoading] = useState(false);
  const [meatSearch, setMeatSearch] = useState('');
  const [meatSelectedCategories, setMeatSelectedCategories] = useState([]);
  const [meatCategories, setMeatCategories] = useState([]);
  const [meatSelectedWeight, setMeatSelectedWeight] = useState(null);
  const [meatMinPrice, setMeatMinPrice] = useState(0);
  const [meatMaxPrice, setMeatMaxPrice] = useState(10000);
  const [meatSelectedButcherId, setMeatSelectedButcherId] = useState('');
  const [meatButchers, setMeatButchers] = useState([]);
  const [meatSortBy, setMeatSortBy] = useState('Price: High to Low');
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const ITEMS_PER_PAGE = 9;
  const [currentPage, setCurrentPage] = useState(1);
  const [meatCurrentPage, setMeatCurrentPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const weightRanges = [
    { label: '30 - 50 kg',  min: 30,  max: 50    },
    { label: '50 - 80 kg',  min: 50,  max: 80    },
    { label: '80 - 120 kg', min: 80,  max: 120   },
    { label: '120kg +',     min: 120, max: 99999 },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search');
    if (q) setSearchTerm(q);

    const seller = params.get('seller_id');
    if (seller) setSelectedSellerId(seller);
    const mode = params.get('mode');     
    if (mode) setBrowseMode(mode);     
  }, [location.search]);

  useEffect(() => {
    const fetchAnimals = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (selectedCategories.length > 0) {
          params.append('category', selectedCategories.join(','));
        }
        if (selectedBreeds.length > 0) params.append('breed', selectedBreeds[0]);
        params.append('max_price', priceRange);
        if (selectedWeight) {
          params.append('min_weight', selectedWeight.min);
          params.append('max_weight', selectedWeight.max);
        }
        if (locationSearch.trim()) params.append('location', locationSearch.trim());
        if (searchTerm.trim()) params.append('search', searchTerm.trim());

        if (selectedSellerId) params.append('seller_id', selectedSellerId);
        if (minPrice > 0) params.append('min_price', minPrice);

        const res = await fetch(`${API_URL}/api/public/browse?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to fetch animals');
        const data = await res.json();

        const sorted = [...data].sort((a, b) => {
          if (sortBy === 'Price: High to Low') return b.price_inr - a.price_inr;
          if (sortBy === 'Price: Low to High') return a.price_inr - b.price_inr;
          if (sortBy === 'Most Recent') return new Date(b.created_at) - new Date(a.created_at);
          if (sortBy === 'Age: Youngest First') return a.age_months - b.age_months;
          return 0;
        });

        if (!selectedSellerId && selectedCategories.length === 0 && selectedBreeds.length === 0) {
          const uniqueCategories = [...new Set(data.map(item => item.category).filter(Boolean))];
          const uniqueBreeds = [...new Set(data.map(item => item.breed).filter(Boolean))];
          const uniqueSellers = [...new Map(data.filter(item => item.seller_id && item.seller_name)
            .map(item => [item.seller_id, { id: item.seller_id, name: item.seller_name }])).values()];
          setCategories(uniqueCategories);
          setBreeds(uniqueBreeds);
          setSellers(uniqueSellers);
        }

        setSearchResults(sorted);
        setCurrentPage(1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnimals();
  }, [selectedCategories, selectedBreeds, priceRange, minPrice, sortBy, selectedWeight, locationSearch, searchTerm, selectedSellerId, API_URL]);

  useEffect(() => {
    if (browseMode !== 'meat') return;
    setMeatLoading(true);

    const params = new URLSearchParams();
    if (meatSelectedCategories.length > 0) params.append('category', meatSelectedCategories[0]);
    if (meatMinPrice > 0) params.append('min_price', meatMinPrice);
    if (meatMaxPrice < 10000) params.append('max_price', meatMaxPrice);
    if (meatSelectedWeight) {
      params.append('min_weight', meatSelectedWeight.min);
      params.append('max_weight', meatSelectedWeight.max);
    }
    if (meatSelectedButcherId) params.append('butcher_id', meatSelectedButcherId);
    if (meatSearch.trim()) params.append('search', meatSearch.trim());

    fetch(`${API_URL}/api/public/browse-meat?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        // Extract dynamic categories and butchers
        const cats = [...new Set(data.map(p => p.category).filter(Boolean))];
        const butchers = [...new Map(data.filter(p => p.butcher_id && p.seller_name)
          .map(p => [p.butcher_id, { id: p.butcher_id, name: p.seller_name }])).values()];
        
        if (!meatSelectedCategories.length && !meatSelectedButcherId && !meatSelectedWeight) {
          setMeatCategories(cats);
          setMeatButchers(butchers);
        }

        const sorted = [...data].sort((a, b) => {
          if (meatSortBy === 'Price: High to Low') return b.price_inr - a.price_inr;
          if (meatSortBy === 'Price: Low to High') return a.price_inr - b.price_inr;
          if (meatSortBy === 'Most Recent') return new Date(b.created_at) - new Date(a.created_at);
          return 0;
        });
        setMeatProducts(sorted);
        setMeatCurrentPage(1);
      })
      .catch(() => {})
      .finally(() => setMeatLoading(false));
  }, [browseMode, meatSelectedCategories, meatMinPrice, meatMaxPrice, meatSelectedWeight, meatSelectedButcherId, meatSearch, meatSortBy, API_URL]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.token) return;
    fetch(`${API_URL}/api/customer/wishlist`, {
      headers: { Authorization: `Bearer ${user.token}` }
    })
      .then(r => r.json())
      .then(data => setWishlistIds(new Set(data.map(a => `${a.type}-${a.id}`))))
      .catch(() => {});
  }, [API_URL]);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const formatPrice = (val) =>
    val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

  return (
    <div className="bg-background text-on-surface font-body">
      <Navbar variant="default" />

      <main className="pt-28 pb-20 px-4 md:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <header className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <nav className="flex items-center space-x-2 text-sm text-neutral-500 mb-4">
                <a onClick={() => navigate('/')} 
                  className="hover:text-primary cursor-pointer transition-colors">
                  Home
                </a>
                <span className="material-symbols-outlined text-xs">chevron_right</span>
                <a onClick={() => navigate('/search')} 
                  className="hover:text-primary cursor-pointer transition-colors text-primary font-medium">
                  Marketplace
                </a>
                <span className="material-symbols-outlined text-xs">chevron_right</span>
                <span className="font-medium">Search Results</span>
              </nav>

              <div className="flex gap-2 bg-surface-container-high rounded-2xl p-1 w-fit mb-4">
                <button onClick={() => setBrowseMode('animals')}
                  className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
                    browseMode === 'animals' ? 'bg-primary text-white shadow-sm' : 'text-secondary hover:bg-surface-container'
                  }`}>
                  🐐 Live Animals
                </button>
                <button onClick={() => setBrowseMode('meat')}
                  className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
                    browseMode === 'meat' ? 'bg-primary text-white shadow-sm' : 'text-secondary hover:bg-surface-container'
                  }`}>
                  🥩 Meat Products
                </button>
              </div>

              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-on-surface mb-2">
                {browseMode === 'meat'
                  ? <>Browse <span className="text-primary">Meat Products</span></>
                  : searchTerm
                    ? <>Results for <span className="text-primary">"{searchTerm}"</span></>
                    : <>Browse <span className="text-primary">Elite Livestock</span></>
                }
              </h1>
              <p className="text-secondary-fixed-dim text-lg">
                {browseMode === 'meat'
                  ? (meatLoading ? 'Loading...' : `Showing ${meatProducts.length} products.`)
                  : (loading ? 'Loading...' : `Showing ${searchResults.length} elite specimens.`)
                }
              </p>
              {browseMode === 'animals' && (
              <div className="mt-4 flex items-center bg-surface-container-high px-4 py-2 rounded-full border border-outline-variant/20 max-w-xl">
                <span className="material-symbols-outlined text-outline-variant mr-3">search</span>
                <input
                  type="text"
                  placeholder="Search by name, breed, description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-transparent border-none text-sm text-on-surface placeholder:text-outline-variant focus:ring-0 w-full outline-none py-1"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="text-outline-variant hover:text-primary transition-colors ml-2">
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                )}
              </div>
              )}
              {browseMode === 'meat' && (
              <div className="mt-4 flex items-center bg-surface-container-high px-4 py-2 rounded-full border border-outline-variant/20 max-w-xl">
                <span className="material-symbols-outlined text-outline-variant mr-3">search</span>
                <input type="text" placeholder="Search meat products..."
                  value={meatSearch} onChange={e => setMeatSearch(e.target.value)}
                  className="bg-transparent border-none text-sm text-on-surface placeholder:text-outline-variant focus:ring-0 w-full outline-none py-1" />
                {meatSearch && (
                  <button onClick={() => setMeatSearch('')} className="text-outline-variant hover:text-primary transition-colors ml-2">
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                )}
              </div>
              )}
            </div>
            {browseMode === 'animals' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center bg-surface-container-high px-4 py-2 rounded-full border border-outline-variant/20">
                <span className="text-sm font-medium text-on-surface-variant mr-3">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent border-none text-sm font-bold text-on-surface focus:ring-0 cursor-pointer"
                >
                  <option>Price: High to Low</option>
                  <option>Price: Low to High</option>
                  <option>Most Recent</option>
                  <option>Age: Youngest First</option>
                </select>
              </div>
            </div>
            )}
            {browseMode === 'meat' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center bg-surface-container-high px-4 py-2 rounded-full border border-outline-variant/20">
                <span className="text-sm font-medium text-on-surface-variant mr-3">Sort by:</span>
                <select value={meatSortBy} onChange={e => setMeatSortBy(e.target.value)}
                  className="bg-transparent border-none text-sm font-bold text-on-surface focus:ring-0 cursor-pointer">
                  <option>Price: High to Low</option>
                  <option>Price: Low to High</option>
                  <option>Most Recent</option>
                </select>
              </div>
            </div>
            )}
          </div>
        </header>

        {/* Mobile: Filter + Sort bar */}
        <div className="flex lg:hidden items-center gap-3 mb-6">
          <button
            onClick={() => setMobileFilterOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-surface-container-high border border-outline-variant/20 text-sm font-bold text-on-surface"
          >
            <span className="material-symbols-outlined text-base">tune</span>
            Filters
            {(selectedCategories.length + selectedBreeds.length + (selectedWeight ? 1 : 0) + (selectedSellerId ? 1 : 0)) > 0 && (
              <span className="bg-primary text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                {selectedCategories.length + selectedBreeds.length + (selectedWeight ? 1 : 0) + (selectedSellerId ? 1 : 0)}
              </span>
            )}
          </button>
          <div className="flex-1 flex items-center bg-surface-container-high px-3 py-3 rounded-2xl border border-outline-variant/20">
            <select
              value={browseMode === 'meat' ? meatSortBy : sortBy}
              onChange={(e) => browseMode === 'meat' ? setMeatSortBy(e.target.value) : setSortBy(e.target.value)}
              className="bg-transparent border-none text-sm font-bold text-on-surface focus:ring-0 cursor-pointer w-full"
            >
              <option>Price: High to Low</option>
              <option>Price: Low to High</option>
              <option>Most Recent</option>
              {browseMode === 'animals' && <option>Age: Youngest First</option>}
            </select>
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        {mobileFilterOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" style={{margin: 0, padding: 0}}>
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileFilterOpen(false)}
            />
            {/* Sheet */}
            <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden w-screen">
              <div className="sticky top-0 bg-background px-6 pt-5 pb-4 border-b border-outline-variant/10 flex items-center justify-between z-10">
                <h2 className="text-lg font-black text-on-surface">Filters</h2>
                <button
                  onClick={() => setMobileFilterOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-surface-container-high"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>

              <div className="px-4 py-6 space-y-8">
                {browseMode === 'animals' ? (
                  <>
                    {/* Seller */}
                    {sellers.length > 0 && (
                      <div>
                        <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Farmer / Seller</h3>
                        <div className="flex flex-wrap gap-2">
                          {sellers.map((seller) => (
                            <button key={seller.id}
                              onClick={() => setSelectedSellerId(prev => prev === String(seller.id) ? '' : String(seller.id))}
                              className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                                selectedSellerId === String(seller.id)
                                  ? 'bg-primary text-white border-primary'
                                  : 'border-outline-variant/30 text-on-surface'
                              }`}>
                              {seller.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Category */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Category</h3>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((cat) => (
                          <button key={cat}
                            onClick={() => toggleCategory(cat)}
                            className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                              selectedCategories.includes(cat)
                                ? 'bg-primary text-white border-primary'
                                : 'border-outline-variant/30 text-on-surface'
                            }`}>
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Breed */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Breed</h3>
                      <div className="flex flex-wrap gap-2">
                        {breeds.map((breed) => (
                          <button key={breed}
                            onClick={() => setSelectedBreeds(prev => prev.includes(breed) ? prev.filter(b => b !== breed) : [...prev, breed])}
                            className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                              selectedBreeds.includes(breed)
                                ? 'bg-primary text-white border-primary'
                                : 'border-outline-variant/30 text-on-surface'
                            }`}>
                            {breed}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Weight */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Weight (kg)</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {weightRanges.map((range) => (
                          <button key={range.label}
                            onClick={() => setSelectedWeight(selectedWeight?.label === range.label ? null : range)}
                            className={`py-2 px-4 rounded-xl border text-sm font-bold transition-all ${
                              selectedWeight?.label === range.label
                                ? 'border-primary bg-primary text-white'
                                : 'border-outline-variant/30 text-on-surface'
                            }`}>
                            {range.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Price */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Price Range (INR)</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <input type="number" value={minPrice} onChange={e => setMinPrice(Number(e.target.value))}
                            placeholder="Min" step="1000" min="0"
                            className="flex-1 bg-surface-container-high border border-outline-variant/20 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-primary" />
                          <span className="text-outline-variant">—</span>
                          <input type="number" value={priceRange} onChange={e => setPriceRange(Number(e.target.value))}
                            placeholder="Max" step="1000" min="0"
                            className="flex-1 bg-surface-container-high border border-outline-variant/20 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Butcher */}
                    {meatButchers.length > 0 && (
                      <div>
                        <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Butcher</h3>
                        <div className="flex flex-wrap gap-2">
                          {meatButchers.map((b) => (
                            <button key={b.id}
                              onClick={() => setMeatSelectedButcherId(prev => prev === String(b.id) ? '' : String(b.id))}
                              className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                                meatSelectedButcherId === String(b.id)
                                  ? 'bg-primary text-white border-primary'
                                  : 'border-outline-variant/30 text-on-surface'
                              }`}>
                              {b.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Meat Category */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Category</h3>
                      <div className="flex flex-wrap gap-2">
                        {meatCategories.map((cat) => (
                          <button key={cat}
                            onClick={() => setMeatSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
                            className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                              meatSelectedCategories.includes(cat)
                                ? 'bg-primary text-white border-primary'
                                : 'border-outline-variant/30 text-on-surface'
                            }`}>
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Meat Weight */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Weight (kg)</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {[{ label: '1 - 5 kg', min: 1, max: 5 }, { label: '5 - 10 kg', min: 5, max: 10 }, { label: '10 - 20 kg', min: 10, max: 20 }, { label: '20kg +', min: 20, max: 9999 }].map((range) => (
                          <button key={range.label}
                            onClick={() => setMeatSelectedWeight(meatSelectedWeight?.label === range.label ? null : range)}
                            className={`py-2 px-4 rounded-xl border text-sm font-bold transition-all ${
                              meatSelectedWeight?.label === range.label
                                ? 'border-primary bg-primary text-white'
                                : 'border-outline-variant/30 text-on-surface'
                            }`}>
                            {range.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Meat Price */}
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4">Price Range (INR)</h3>
                      <div className="flex items-center gap-2">
                        <input type="number" value={meatMinPrice} onChange={e => setMeatMinPrice(Number(e.target.value))}
                          placeholder="Min" step="100" min="0"
                          className="flex-1 bg-surface-container-high border border-outline-variant/20 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-primary" />
                        <span className="text-outline-variant">—</span>
                        <input type="number" value={meatMaxPrice} onChange={e => setMeatMaxPrice(Number(e.target.value))}
                          placeholder="Max" step="100" min="0"
                          className="flex-1 bg-surface-container-high border border-outline-variant/20 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Apply button */}
              <div className="sticky bottom-0 bg-background px-6 py-4 border-t border-outline-variant/10">
                <button
                  onClick={() => setMobileFilterOpen(false)}
                  className="w-full py-4 bg-gradient-to-r from-primary to-primary-container text-white rounded-2xl font-bold text-sm tracking-wide shadow-lg shadow-primary/20">
                  Show Results
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          {browseMode === 'animals' && (
          <aside className="hidden lg:block w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-28 space-y-10">

              {/* Seller Filter */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Farmer / Seller</h3>
                <div className="space-y-4">
                  {sellers.map((seller) => (
                    <label key={seller.id} className="flex items-center group cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSellerId === String(seller.id)}
                        onChange={() => setSelectedSellerId(prev => prev === String(seller.id) ? '' : String(seller.id))}
                        className="w-5 h-5 rounded border-outline-variant text-primary focus:ring-primary/20 transition-all"
                      />
                      <span className="ml-3 text-on-surface font-medium group-hover:text-primary transition-colors">
                        {seller.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Category</h3>
                <div className="space-y-4">
                  {categories.map((cat) => (
                    <label key={cat} className="flex items-center group cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => toggleCategory(cat)}
                        className="w-5 h-5 rounded border-outline-variant text-primary focus:ring-primary/20 transition-all"
                      />
                      <span className="ml-3 text-on-surface font-medium group-hover:text-primary transition-colors">
                        {cat}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Breed Filter */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Breed</h3>
                <div className="space-y-4">
                  {breeds.map((breed) => (
                    <label key={breed} className="flex items-center group cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBreeds.includes(breed)}
                        onChange={() =>
                          setSelectedBreeds((prev) =>
                            prev.includes(breed) ? prev.filter((b) => b !== breed) : [...prev, breed]
                          )
                        }
                        className="w-5 h-5 rounded border-outline-variant text-primary focus:ring-primary/20 transition-all"
                      />
                      <span className="ml-3 text-on-surface font-medium group-hover:text-primary transition-colors">
                        {breed}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range Filter */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Price Range (INR)</h3>

                {/* Slider for max price */}
                <div className="px-2 space-y-4">
                {/* Min/Max inputs for precise control */}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <p className="text-[10px] font-bold text-on-surface-variant mb-1">Min</p>
                    <input
                      type="number"
                      value={minPrice}
                      onChange={(e) => setMinPrice(Number(e.target.value))}
                      className="w-full bg-surface-container-high border border-outline-variant/20 rounded-lg px-2 py-1.5 text-xs font-bold text-on-surface focus:ring-1 focus:ring-primary outline-none"
                      step="1000"
                      min="0"
                    />
                  </div>
                  <span className="text-outline-variant mt-4">—</span>
                  <div className="flex-1">
                    <p className="text-[10px] font-bold text-on-surface-variant mb-1">Max</p>
                    <input
                      type="number"
                      value={priceRange}
                      onChange={(e) => setPriceRange(Number(e.target.value))}
                      className="w-full bg-surface-container-high border border-outline-variant/20 rounded-lg px-2 py-1.5 text-xs font-bold text-on-surface focus:ring-1 focus:ring-primary outline-none"
                      step="1000"
                      min="0"
                    />
                  </div>
                </div>

                {/* Min slider */}
                <div>
                  <p className="text-[10px] font-bold text-on-surface-variant mb-1">Min: {formatPrice(minPrice)}</p>
                  <input
                    type="range"
                    min="0"
                    max="500000"
                    step="1000"
                    value={minPrice}
                    onChange={(e) => setMinPrice(Number(e.target.value))}
                    className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                </div>

                {/* Max slider */}
                <div>
                  <p className="text-[10px] font-bold text-on-surface-variant mb-1">Max: {priceRange >= 500000 ? '₹5L+' : formatPrice(priceRange)}</p>
                  <input
                    type="range"
                    min="0"
                    max="500000"
                    step="1000"
                    value={priceRange}
                    onChange={(e) => setPriceRange(Number(e.target.value))}
                    className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                </div>

                {/* +/- fine controls */}
                <div className="flex gap-2">
                  <button onClick={() => setMinPrice(p => Math.max(0, p - 1000))} className="flex-1 py-1.5 rounded-lg border border-outline-variant/30 text-xs font-bold hover:border-primary hover:text-primary transition-all">Min −</button>
                  <button onClick={() => setMinPrice(p => p + 1000)} className="flex-1 py-1.5 rounded-lg border border-outline-variant/30 text-xs font-bold hover:border-primary hover:text-primary transition-all">Min +</button>
                  <button onClick={() => setPriceRange(p => Math.max(minPrice, p - 1000))} className="flex-1 py-1.5 rounded-lg border border-outline-variant/30 text-xs font-bold hover:border-primary hover:text-primary transition-all">Max −</button>
                  <button onClick={() => setPriceRange(p => p + 1000)} className="flex-1 py-1.5 rounded-lg border border-outline-variant/30 text-xs font-bold hover:border-primary hover:text-primary transition-all">Max +</button>
                </div>
              </div>
              </div>

              {/* Weight Filter */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Weight (kg)</h3>
                <div className="grid grid-cols-2 gap-2">
                  {weightRanges.map((range) => (
                    <button
                      key={range.label}
                      onClick={() => setSelectedWeight(selectedWeight?.label === range.label ? null : range)}
                      className={`py-2 px-4 rounded-xl border text-xs font-bold transition-all
                        ${selectedWeight?.label === range.label
                          ? 'border-primary bg-primary-fixed text-on-primary-fixed'
                          : 'border-outline-variant/30 text-on-surface hover:border-primary/50'
                        }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Filter */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Location</h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Zip code or City"
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="w-full bg-surface-container-high border-none rounded-xl py-3 pl-10 pr-4 text-sm focus:ring-2 focus:ring-primary transition-all"
                  />
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline-variant">
                    location_on
                  </span>
                </div>
              </div>

            </div>
          </aside>
          )}

          {browseMode === 'meat' && (
            <aside className="hidden lg:block w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-28 space-y-10">

                {/* Butcher Filter */}
                <div>
                  <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Butcher</h3>
                  <div className="space-y-4">
                    {meatButchers.map((b) => (
                      <label key={b.id} className="flex items-center group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={meatSelectedButcherId === String(b.id)}
                          onChange={() => setMeatSelectedButcherId(prev => prev === String(b.id) ? '' : String(b.id))}
                          className="w-5 h-5 rounded border-outline-variant text-primary focus:ring-primary/20"
                        />
                        <span className="ml-3 text-on-surface font-medium group-hover:text-primary transition-colors">
                          {b.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Category</h3>
                  <div className="space-y-4">
                    {meatCategories.map((cat) => (
                      <label key={cat} className="flex items-center group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={meatSelectedCategories.includes(cat)}
                          onChange={() => setMeatSelectedCategories(prev =>
                            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
                          )}
                          className="w-5 h-5 rounded border-outline-variant text-primary focus:ring-primary/20"
                        />
                        <span className="ml-3 text-on-surface font-medium group-hover:text-primary transition-colors">
                          {cat}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Price Range (INR)</h3>
                  <div className="px-2 space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <p className="text-[10px] font-bold text-on-surface-variant mb-1">Min</p>
                        <input type="number" value={meatMinPrice}
                          onChange={e => setMeatMinPrice(Number(e.target.value))}
                          className="w-full bg-surface-container-high border border-outline-variant/20 rounded-lg px-2 py-1.5 text-xs font-bold text-on-surface focus:ring-1 focus:ring-primary outline-none"
                          step="100" min="0" />
                      </div>
                      <span className="text-outline-variant mt-4">—</span>
                      <div className="flex-1">
                        <p className="text-[10px] font-bold text-on-surface-variant mb-1">Max</p>
                        <input type="number" value={meatMaxPrice}
                          onChange={e => setMeatMaxPrice(Number(e.target.value))}
                          className="w-full bg-surface-container-high border border-outline-variant/20 rounded-lg px-2 py-1.5 text-xs font-bold text-on-surface focus:ring-1 focus:ring-primary outline-none"
                          step="100" min="0" />
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-on-surface-variant mb-1">Min: ₹{meatMinPrice.toLocaleString('en-IN')}</p>
                      <input type="range" min="0" max="10000" step="100" value={meatMinPrice}
                        onChange={e => setMeatMinPrice(Number(e.target.value))}
                        className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-on-surface-variant mb-1">Max: ₹{meatMaxPrice.toLocaleString('en-IN')}</p>
                      <input type="range" min="0" max="10000" step="100" value={meatMaxPrice}
                        onChange={e => setMeatMaxPrice(Number(e.target.value))}
                        className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary" />
                    </div>
                  </div>
                </div>

                {/* Weight Filter */}
                <div>
                  <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-6">Weight (kg)</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: '1 - 5 kg',   min: 1,  max: 5   },
                      { label: '5 - 10 kg',  min: 5,  max: 10  },
                      { label: '10 - 20 kg', min: 10, max: 20  },
                      { label: '20kg +',     min: 20, max: 9999 },
                    ].map((range) => (
                      <button key={range.label}
                        onClick={() => setMeatSelectedWeight(meatSelectedWeight?.label === range.label ? null : range)}
                        className={`py-2 px-4 rounded-xl border text-xs font-bold transition-all
                          ${meatSelectedWeight?.label === range.label
                            ? 'border-primary bg-primary-fixed text-on-primary-fixed'
                            : 'border-outline-variant/30 text-on-surface hover:border-primary/50'
                          }`}>
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reset */}
                <button
                  onClick={() => {
                    setMeatSelectedCategories([]);
                    setMeatSelectedWeight(null);
                    setMeatMinPrice(0);
                    setMeatMaxPrice(10000);
                    setMeatSelectedButcherId('');
                    setMeatSearch('');
                  }}
                  className="w-full py-2 rounded-xl border border-outline-variant/30 text-xs font-bold text-on-surface-variant hover:border-primary hover:text-primary transition-all">
                  Reset Filters
                </button>

              </div>
            </aside>
          )}

          {/* Results */}
          <section className="flex-1">
            {browseMode === 'meat' ? (
              <>
                {meatLoading ? (
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 md:gap-8">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="bg-surface-container-lowest rounded-xl overflow-hidden animate-pulse">
                        <div className="h-64 bg-surface-container-high" />
                        <div className="p-6 space-y-3">
                          <div className="h-4 bg-surface-container-high rounded w-3/4" />
                          <div className="h-4 bg-surface-container-high rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 md:gap-8">
                    {meatProducts.slice((meatCurrentPage - 1) * ITEMS_PER_PAGE, meatCurrentPage * ITEMS_PER_PAGE).map(p => (
                      <div key={p.id}
                        onClick={() => navigate(`/meat-product/${p.public_id || p.id}`)}
                        className="bg-surface-container-lowest rounded-xl overflow-hidden editorial-shadow group border border-transparent hover:border-primary-fixed-dim/30 transition-all duration-500 cursor-pointer flex flex-col">
                        <div className="relative h-32 md:h-64 overflow-hidden">
                          {p.image_url
                            ? <img src={p.image_url.startsWith('http') ? p.image_url : `${API_URL}${p.image_url}`}
                                alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                            : <div className="w-full h-full bg-surface-container flex items-center justify-center">
                                <span className="material-symbols-outlined text-[64px] text-outline">storefront</span>
                              </div>
                          }
                          <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-white/90 backdrop-blur-md px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[8px] md:text-[10px] font-black tracking-widest uppercase text-primary">
                            {p.category}
                          </div>
                          <span className={`absolute top-2 right-2 md:top-4 md:right-4 text-[8px] md:text-[10px] font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full ${
                            p.stock_qty > 0 ? 'bg-black/60 text-white' : 'bg-orange-500 text-white'
                          }`}>
                            {p.stock_qty > 0 ? `Stock: ${p.stock_qty}` : 'Out of Stock'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const user = JSON.parse(localStorage.getItem('user') || '{}');
                              if (!user.token) { navigate('/login'); return; }
                              const alreadySaved = wishlistIds.has(`meat-${p.id}`);
                              fetch(`${API_URL}/api/customer/wishlist/${p.id}`, {
                                method: alreadySaved ? 'DELETE' : 'POST',
                                headers: {
                                  Authorization: `Bearer ${user.token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ item_type: 'meat' })
                              }).then(() => {
                                setWishlistIds(prev => {
                                  const next = new Set(prev);
                                  alreadySaved ? next.delete(`meat-${p.id}`) : next.add(`meat-${p.id}`);
                                  return next;
                                });
                              }).catch(() => {});
                            }}
                            className="absolute top-12 right-4 transition-colors"
                          >
                            <span
                              className={`material-symbols-outlined text-2xl ${
                                wishlistIds.has(`meat-${p.id}`) ? 'text-red-500' : 'text-white'
                              }`}
                              style={{ fontVariationSettings: wishlistIds.has(`meat-${p.id}`) ? "'FILL' 1" : "'FILL' 0" }}
                            >
                              favorite
                            </span>
                          </button>
                        </div>
                        <div className="p-3 md:p-6 flex flex-col flex-1">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-xs md:text-xl font-bold text-on-surface leading-tight line-clamp-2">{p.name}</h3>
                              <p className="text-[10px] md:text-xs font-medium text-neutral-500">{p.weight_kg ? `${p.weight_kg} ${p.unit || 'kg'}` : p.unit || ''}</p>
                            </div>
                            <span className="text-sm md:text-2xl font-black text-primary tracking-tighter whitespace-nowrap">
                              ₹{Number(p.price_inr).toLocaleString('en-IN')}
                            </span>
                          </div>
                          {(p.location_city || p.location_state) && (
                            <div className="flex items-center gap-2 py-3 border-y border-outline-variant/10 mb-4">
                              <span className="material-symbols-outlined text-primary-container text-sm">location_on</span>
                              <span className="text-xs font-bold text-on-surface-variant">
                                {[p.location_city, p.location_state].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                          <button 
                            onClick={(e) => { e.stopPropagation(); navigate(`/meat-product/${p.public_id || p.id}`); }}
                            className="mt-auto w-full py-2 md:py-4 text-xs md:text-sm bg-gradient-to-r from-primary to-primary-container text-white rounded-full font-bold text-sm tracking-wide shadow-lg shadow-primary/20 scale-[0.98] active:scale-95 transition-all hover:shadow-primary/40">
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
            {error && (
              <div className="text-red-500 text-center py-20 font-semibold">{error}</div>
            )}

            {loading ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 md:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-surface-container-lowest rounded-xl overflow-hidden animate-pulse">
                    <div className="h-64 bg-surface-container-high" />
                    <div className="p-6 space-y-3">
                      <div className="h-4 bg-surface-container-high rounded w-3/4" />
                      <div className="h-4 bg-surface-container-high rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 md:gap-8">
                {searchResults.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/product/${item.public_id || item.id}`)}
                    className="bg-surface-container-lowest rounded-xl overflow-hidden editorial-shadow group border border-transparent hover:border-primary-fixed-dim/30 transition-all duration-500 cursor-pointer flex flex-col"
                  >
                    <div className="relative h-32 md:h-64 overflow-hidden">
                      <img
                        src={item.image_url || '/assets/placeholder.png'}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-white/90 backdrop-blur-md px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[8px] md:text-[10px] font-black tracking-widest uppercase text-primary">
                        {item.category}
                      </div>
                      {item.status === 'sold' ? (
                        <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 bg-orange-500 text-white text-[8px] md:text-[10px] font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                          Sold Out
                        </span>
                      ) : (
                        <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 bg-black/60 text-white text-[8px] md:text-[10px] font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                          {item.quantity} left
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const user = JSON.parse(localStorage.getItem('user') || '{}');
                          if (!user.token) { navigate('/login'); return; }
                          const alreadySaved = wishlistIds.has(`animal-${item.id}`);
                          fetch(`${API_URL}/api/customer/wishlist/${item.id}`, {
                            method: alreadySaved ? 'DELETE' : 'POST',
                            headers: { Authorization: `Bearer ${user.token}` }
                          }).then(() => {
                            setWishlistIds(prev => {
                              const next = new Set(prev);
                              alreadySaved ? next.delete(`animal-${item.id}`) : next.add(`animal-${item.id}`);
                              return next;
                            });
                          }).catch(() => {});
                        }}
                        className="absolute top-2 right-2 md:top-4 md:right-4 transition-colors"
                      >
                        <span
                          className={`material-symbols-outlined text-2xl ${
                            wishlistIds.has(`animal-${item.id}`) ? 'text-red-500' : 'text-white'
                          }`}
                          style={{ fontVariationSettings: wishlistIds.has(`animal-${item.id}`) ? "'FILL' 1" : "'FILL' 0" }}
                        >
                          favorite
                        </span>
                      </button>
                    </div>

                    <div className="p-3 md:p-6 flex flex-col flex-1">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xs md:text-xl font-bold text-on-surface leading-tight line-clamp-2">{item.name}</h3>
                          <p className="text-[10px] md:text-xs font-medium text-neutral-500">{item.breed}</p>
                        </div>
                        <span className="text-sm md:text-2xl font-black text-primary tracking-tighter whitespace-nowrap">
                          {formatPrice(item.price_inr)}
                        </span>
                      </div>

                      <div className="hidden md:grid grid-cols-2 gap-4 py-4 border-y border-outline-variant/10 mb-6">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary-container text-sm">fitness_center</span>
                          <span className="text-xs font-bold text-on-surface-variant">{item.weight_kg} kg</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary-container text-sm">calendar_month</span>
                          <span className="text-xs font-bold text-on-surface-variant">{item.age_months} Months</span>
                        </div>
                        <div className="flex items-center gap-2 col-span-2">
                          <span className="material-symbols-outlined text-primary-container text-sm">location_on</span>
                          <span className="text-xs font-bold text-on-surface-variant">
                            {item.location_city}{item.location_state ? `, ${item.location_state}` : ''}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/product/${item.public_id || item.id}`); }}
                        className="mt-auto w-full py-2 md:py-4 text-xs md:text-sm bg-gradient-to-r from-primary to-primary-container text-white rounded-full font-bold text-sm tracking-wide shadow-lg shadow-primary/20 scale-[0.98] active:scale-95 transition-all hover:shadow-primary/40"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {Math.ceil(searchResults.length / ITEMS_PER_PAGE) > 1 && (
              <div className="mt-20 flex justify-center items-center gap-4">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-surface-container-high text-on-surface hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>

                {Array.from({ length: Math.ceil(searchResults.length / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-12 h-12 flex items-center justify-center rounded-full font-bold transition-all ${
                      currentPage === page
                        ? 'bg-primary text-white'
                        : 'bg-surface-container-high text-on-surface hover:bg-primary-container hover:text-white'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(searchResults.length / ITEMS_PER_PAGE), p + 1))}
                  disabled={currentPage === Math.ceil(searchResults.length / ITEMS_PER_PAGE)}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-surface-container-high text-on-surface hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
             </>
            )}
          </section>
        </div>
      </main>

      <Footer variant="default" />
    </div>
  );
};

export default SearchResults;